import { useEffect, useMemo, useState } from "react";
import { listAgents, deleteAgent, cloneAgent } from "../api/agentApi";
import { BRAND_COLORS, PAGE_SHELL, PAGE_TYPOGRAPHY, UI_RADIUS } from "../constants";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "./UserContext";
import { alpha, useTheme } from "@mui/material/styles";
import { PERMISSIONS } from "../permissions";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const COPPER = BRAND_COLORS.primary.main;
const COPPER_DARK = BRAND_COLORS.primary.dark;
const COPPER_SOFT = BRAND_COLORS.primary.soft;
const BORDER_SOFT = "#E5D4C4";
const CATALOG_GREEN = "#2F7D4A";
const CATALOG_GREEN_SOFT = "rgba(47, 125, 74, 0.12)";
const MUTED_TEXT = "#6F6A64";
const API_SETUP_BLUE = "#1F78D1";
const API_SETUP_BLUE_SOFT = "rgba(31, 120, 209, 0.12)";
const DARK_COPPER_TEXT = "#F2C69B";
const DARK_MUTED_TEXT = "#D9C9BA";
const DARK_PANEL = "#181411";
const DARK_PANEL_SOFT = "#211B16";
const DARK_BORDER = "rgba(224,177,132,0.24)";

const chipBaseSx = {
  borderRadius: UI_RADIUS.chip,
};

const formControlSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: UI_RADIUS.input,
  },
};

const primaryActionButtonSx = {
  borderRadius: UI_RADIUS.button,
  bgcolor: "primary.main",
  boxShadow: `0 10px 24px ${BRAND_COLORS.primary.shadow}`,
  color: "#fff",
  textTransform: "none",
  fontWeight: 900,
  minHeight: 44,
  minWidth: { sm: 168 },
  px: 2.4,
  width: { xs: "100%", sm: "auto" },
  "&:hover": {
    bgcolor: "primary.dark",
    boxShadow: `0 14px 30px ${BRAND_COLORS.primary.shadowHover}`,
  },
};

const COLLECTIONS = [
  {
    id: "marketing_growth",
    label: "Marketing & Growth",
    description: "Campaigns, launches, content, SEO, social, brand, and paid media agents.",
    keywords: ["marketing", "campaign", "launch", "content", "seo", "social", "brand", "persona", "press", "ads", "growth"],
  },
  {
    id: "creative_studio",
    label: "Creative Studio",
    description: "Image, video, design, presentation, creative direction, and media production agents.",
    keywords: ["image", "video", "visual", "creative", "designer", "design", "presentation", "slides", "media", "storyboard"],
  },
  {
    id: "sales_revenue",
    label: "Sales & Revenue",
    description: "Sales enablement, proposals, outreach, CRM, account planning, and revenue support.",
    keywords: ["sales", "enablement", "proposal", "revenue", "crm", "account", "outreach", "pipeline"],
  },
  {
    id: "research_intelligence",
    label: "Research & Intelligence",
    description: "Market research, competitive intelligence, analysis, synthesis, and knowledge discovery.",
    keywords: ["research", "competitive", "competitor", "intelligence", "analyst", "analysis", "market", "insight", "benchmark"],
  },
  {
    id: "productivity_office",
    label: "Productivity & Office",
    description: "Summaries, reports, documents, spreadsheets, calendars, plans, and operational writing.",
    keywords: ["summary", "document", "docx", "pdf", "spreadsheet", "xlsx", "report", "office", "calendar", "planner", "brief", "writer"],
  },
  {
    id: "operations_support",
    label: "Operations & Support",
    description: "Customer support, service, operations, tickets, processes, and coordination agents.",
    keywords: ["support", "customer", "service", "ticket", "operations", "ops", "process", "sop", "coordination"],
  },
  {
    id: "developer_automation",
    label: "Developer & Automation",
    description: "Code, API, automation, integration, diagnostics, and developer productivity agents.",
    keywords: ["code", "developer", "api", "automation", "integration", "webhook", "diagnostic", "debug"],
  },
];

const CUSTOM_COLLECTION = {
  id: "custom_agents",
  label: "Custom Agents",
  description: "Agents created, cloned, or generated by your team.",
  keywords: [],
};

const OTHER_COLLECTION = {
  id: "other_agents",
  label: "Other Agents",
  description: "General agents that do not yet map to a specific collection.",
  keywords: [],
};

function normalizeStatus(status) {
  return String(status || "").trim().toLowerCase();
}

function normalizeStatusFilter(value) {
  const status = normalizeStatus(value);
  return ["all", "ready", "setup_required", "building", "created"].includes(status) ? status : "all";
}

function agentNeedsApiSetup(agent) {
  if (normalizeStatus(agent?.status) === "ready") return false;
  return normalizeStatus(agent?.status) === "setup_required" || Boolean(agent?.catalog_metadata?.requires_api_setup);
}

function getStatusLabel(agent) {
  if (agentNeedsApiSetup(agent)) return "API setup needed";
  const status = normalizeStatus(agent?.status);
  if (!status) return "Unverified";
  return status.replace(/_/g, " ");
}

function getStatusChipSx(agent, isDark = false) {
  if (agentNeedsApiSetup(agent)) {
    return {
      ...chipBaseSx,
      bgcolor: isDark ? "rgba(255,184,89,0.16)" : API_SETUP_BLUE_SOFT,
      color: isDark ? "#FFE2BD" : API_SETUP_BLUE,
      border: `1px solid ${isDark ? "rgba(255,184,89,0.30)" : "rgba(31,120,209,0.34)"}`,
    };
  }
  const status = normalizeStatus(agent?.status);
  if (status === "ready") {
    return {
      ...chipBaseSx,
      bgcolor: isDark ? "rgba(91,160,92,0.18)" : CATALOG_GREEN_SOFT,
      color: isDark ? "#CFEFD4" : CATALOG_GREEN,
      border: `1px solid ${isDark ? "rgba(145,205,153,0.30)" : "rgba(47,125,74,0.28)"}`,
    };
  }
  if (status === "building" || status === "pending") {
    return {
      ...chipBaseSx,
      bgcolor: isDark ? "rgba(224,177,132,0.16)" : COPPER_SOFT,
      color: isDark ? DARK_COPPER_TEXT : COPPER_DARK,
      border: `1px solid ${isDark ? "rgba(224,177,132,0.30)" : "rgba(184,115,51,0.24)"}`,
    };
  }
  return {
    ...chipBaseSx,
    bgcolor: isDark ? "rgba(255,247,239,0.10)" : "#F1EEE9",
    color: isDark ? DARK_MUTED_TEXT : MUTED_TEXT,
    border: `1px solid ${isDark ? "rgba(255,247,239,0.16)" : "rgba(111,106,100,0.16)"}`,
  };
}

function normalizeCollectionValue(value) {
  if (!value) return "";
  return String(value).toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

function getAgentText(agent) {
  return [
    agent.name,
    agent.display_name,
    agent.description,
    agent.category,
    agent.collection,
    agent.agent_category,
    agent.agent_collection,
    agent.catalog_collection,
    ...(agent.tags || []),
  ].filter(Boolean).join(" ").toLowerCase();
}

function inferCollection(agent) {
  const metadata = agent.catalog_metadata || {};
  const explicitValues = [
    agent.category,
    agent.collection,
    agent.agent_category,
    agent.agent_collection,
    agent.catalog_collection,
    metadata.category,
    metadata.collection,
  ];
  for (const value of explicitValues) {
    const normalized = normalizeCollectionValue(value);
    const exact = COLLECTIONS.find((collection) => collection.id === normalized || normalizeCollectionValue(collection.label) === normalized);
    if (exact) return exact;
  }

  const text = getAgentText(agent);
  const inferred = COLLECTIONS.find((collection) => collection.keywords.some((keyword) => text.includes(keyword)));
  if (inferred) return inferred;
  if (agent.agent_source && agent.agent_source !== "catalog") return CUSTOM_COLLECTION;
  return OTHER_COLLECTION;
}

function groupAgents(agents) {
  const groups = new Map();
  for (const agent of agents) {
    const collection = inferCollection(agent);
    const existing = groups.get(collection.id) || {
      ...collection,
      agents: [],
      readyCount: 0,
      setupCount: 0,
      builtInCount: 0,
      customCount: 0,
    };
    existing.agents.push(agent);
    if (normalizeStatus(agent.status) === "ready") existing.readyCount += 1;
    if (agentNeedsApiSetup(agent)) existing.setupCount += 1;
    if (agent.agent_source === "catalog") existing.builtInCount += 1;
    else existing.customCount += 1;
    groups.set(collection.id, existing);
  }
  const order = [...COLLECTIONS.map((collection) => collection.id), CUSTOM_COLLECTION.id, OTHER_COLLECTION.id];
  return Array.from(groups.values()).sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
}

function CollectionIcon({ id }) {
  const sx = { fontSize: 22 };
  if (id === "marketing_growth") return <AutoAwesomeIcon sx={sx} />;
  if (id === "creative_studio") return <UploadFileIcon sx={sx} />;
  if (id === "sales_revenue") return <AssignmentIndIcon sx={sx} />;
  if (id === "research_intelligence") return <SearchIcon sx={sx} />;
  if (id === "productivity_office") return <DescriptionOutlinedIcon sx={sx} />;
  if (id === "operations_support") return <AccountTreeIcon sx={sx} />;
  return <SmartToyOutlinedIcon sx={sx} />;
}

function CollectionCard({ group, onOpen, viewMode }) {
  const list = viewMode === "list";
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const borderColor = isDark ? DARK_BORDER : BORDER_SOFT;
  const cardBg = isDark ? DARK_PANEL : "#FFFEFC";
  const iconBg = isDark ? DARK_PANEL_SOFT : COPPER_SOFT;
  const copperText = isDark ? DARK_COPPER_TEXT : COPPER_DARK;
  const mutedText = isDark ? "text.secondary" : MUTED_TEXT;
  return (
    <Card
      elevation={0}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      sx={{
        cursor: "pointer",
        minHeight: list ? 0 : 184,
        borderRadius: UI_RADIUS.card,
        border: `1px solid ${borderColor}`,
        bgcolor: cardBg,
        color: "text.primary",
        boxShadow: isDark ? "0 18px 54px rgba(0,0,0,0.34)" : "0 12px 28px rgba(74,47,25,0.06)",
        transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
        "&:hover, &:focus-visible": {
          outline: "none",
          transform: "translateY(-2px)",
          borderColor: isDark ? "rgba(224,177,132,0.38)" : COPPER,
          boxShadow: isDark ? "0 22px 60px rgba(0,0,0,0.40)" : "0 20px 42px rgba(74,47,25,0.12)",
        },
      }}
    >
      <CardContent sx={{ p: list ? 2 : 2.25 }}>
        <Stack direction={list ? "row" : "column"} alignItems={list ? "center" : "stretch"} justifyContent="space-between" spacing={list ? 2 : 0}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
              <Box sx={{ width: 46, height: 46, borderRadius: UI_RADIUS.panel, bgcolor: iconBg, color: copperText, display: "grid", placeItems: "center", flexShrink: 0 }}>
                <CollectionIcon id={group.id} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 900, minWidth: 0, flex: 1 }} noWrap={list}>
                {group.label}
              </Typography>
            </Stack>
            <Box sx={{ ml: 7.25 }}>
              <Typography variant="body2" sx={{ mt: 0.75, color: mutedText, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: list ? 1 : 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                {group.description}
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center" justifyContent={list ? "flex-end" : "flex-start"} sx={{ mt: list ? 0 : 1.75, ml: list ? 0 : 7.25, flexShrink: 0 }}>
            {group.readyCount > 0 && (
              <Chip size="small" label={`${group.readyCount} ready`} sx={{ ...chipBaseSx, bgcolor: isDark ? "rgba(91,160,92,0.18)" : CATALOG_GREEN_SOFT, color: isDark ? "#CFEFD4" : CATALOG_GREEN, fontWeight: 800 }} />
            )}
            {group.setupCount > 0 && (
              <Chip size="small" label={`${group.setupCount} setup`} sx={{ ...chipBaseSx, bgcolor: isDark ? "rgba(255,184,89,0.16)" : API_SETUP_BLUE_SOFT, color: isDark ? "#FFE2BD" : API_SETUP_BLUE, fontWeight: 800 }} />
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

const getAgentCardActionSx = (isDark) => ({
  width: 34,
  height: 34,
  borderRadius: UI_RADIUS.iconButton,
  border: `1px solid ${isDark ? DARK_BORDER : BORDER_SOFT}`,
  color: isDark ? DARK_MUTED_TEXT : MUTED_TEXT,
  bgcolor: isDark ? alpha("#FFF7EF", 0.06) : "#fff",
  "&:hover": {
    color: isDark ? DARK_COPPER_TEXT : COPPER_DARK,
    borderColor: isDark ? "rgba(224,177,132,0.42)" : COPPER,
    bgcolor: isDark ? "rgba(224,177,132,0.12)" : COPPER_SOFT,
  },
  "&.Mui-disabled": {
    bgcolor: isDark ? "rgba(255,247,239,0.05)" : "#F4F1EE",
    borderColor: isDark ? "rgba(255,247,239,0.12)" : "#E7DED5",
  },
});

export function AgentCard({ agent, viewMode, onDelete, deleting, highlighted = false }) {
  const title = agent.display_name || agent.name || agent.agent_id;
  const list = viewMode === "list";
  const navigate = useNavigate();
  const { can } = useUser();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [cloning, setCloning] = useState(false);
  const borderColor = isDark ? DARK_BORDER : BORDER_SOFT;
  const cardBg = isDark ? DARK_PANEL : "#FFFEFC";
  const mutedText = isDark ? "text.secondary" : MUTED_TEXT;
  const copperText = isDark ? DARK_COPPER_TEXT : COPPER_DARK;
  const greenText = isDark ? "#CFEFD4" : CATALOG_GREEN;
  const greenBg = isDark ? "rgba(91,160,92,0.18)" : CATALOG_GREEN_SOFT;
  const copperBg = isDark ? "rgba(224,177,132,0.16)" : COPPER_SOFT;
  const actionSx = getAgentCardActionSx(isDark);
  const canRunAgents = Boolean(can?.(PERMISSIONS.AGENTS_RUN));
  const canUpdateAgents = Boolean(can?.(PERMISSIONS.AGENTS_UPDATE));
  const canCreateAgents = Boolean(can?.(PERMISSIONS.AGENTS_CREATE));
  const canDeleteAgents = Boolean(can?.(PERMISSIONS.AGENTS_DELETE));

  const handleClone = async () => {
    setCloning(true);
    try {
      const result = await cloneAgent(agent.agent_id, agent.version ?? null);
      const clonedAgentId = result?.agent_id;
      if (clonedAgentId) {
        navigate(`/agent/${clonedAgentId}`);
      }
    } catch (error) {
      window.alert("Failed to clone agent.");
    } finally {
      setCloning(false);
    }
  };

  return (
    <Card
      elevation={0}
      id={`agent-card-${agent.agent_id}`}
      data-testid={`agent-card-${agent.agent_id}`}
      data-highlighted={highlighted ? "true" : undefined}
      sx={{
        display: "flex",
        flexDirection: list ? "row" : "column",
        alignItems: list ? "center" : "stretch",
        gap: list ? 2 : 0,
        height: "100%",
        borderRadius: UI_RADIUS.card,
        border: `1px solid ${borderColor}`,
        borderColor: highlighted ? (isDark ? "#F2C69B" : COPPER) : borderColor,
        outline: highlighted ? `4px solid ${isDark ? "rgba(242,198,155,0.38)" : "rgba(184,115,51,0.32)"}` : "4px solid transparent",
        outlineOffset: 4,
        bgcolor: cardBg,
        color: "text.primary",
        boxShadow: highlighted
          ? isDark
            ? "0 0 0 8px rgba(242,198,155,0.14), 0 28px 80px rgba(242,198,155,0.30)"
            : "0 0 0 8px rgba(184,115,51,0.14), 0 26px 68px rgba(184,115,51,0.32)"
          : isDark
            ? "0 18px 54px rgba(0,0,0,0.34)"
            : "0 10px 26px rgba(31,24,18,0.06)",
        p: list ? 2 : 0,
        scrollMarginTop: 96,
        transform: highlighted ? "translateY(-2px)" : "none",
        transition: "border-color 180ms ease, outline-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
      }}
    >
      <CardContent sx={{ p: list ? 0 : 2.5, flex: 1, minWidth: 0 }}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: UI_RADIUS.iconButton,
                bgcolor: agent.agent_source === "catalog" ? greenBg : copperBg,
                color: agent.agent_source === "catalog" ? greenText : copperText,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <SmartToyOutlinedIcon />
            </Box>
            <Typography
              component={Link}
              to={`/agent/${agent.agent_id}`}
              sx={{ color: "text.primary", textDecoration: "none", fontWeight: 900, display: "block", minWidth: 0, flex: 1 }}
              noWrap={list}
            >
              {title}
            </Typography>
          </Stack>
          <Box sx={{ ml: 6.75 }}>
            <Typography variant="body2" sx={{ color: mutedText, mt: 1, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: list ? 1 : 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {agent.description || "No description provided."}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.75, ml: 6.75 }}>
          {highlighted && (
            <Chip
              size="small"
              label="Newly created"
              sx={{
                ...chipBaseSx,
                bgcolor: isDark ? "rgba(242,198,155,0.22)" : "rgba(184,115,51,0.16)",
                color: copperText,
                border: `1px solid ${isDark ? "rgba(242,198,155,0.48)" : "rgba(184,115,51,0.42)"}`,
                fontWeight: 900,
              }}
            />
          )}
          <Chip size="small" label={getStatusLabel(agent)} sx={{ ...getStatusChipSx(agent, isDark), fontWeight: 850 }} />
        </Stack>
      </CardContent>
      <Stack
        direction="row"
        spacing={0.75}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        justifyContent={list ? "flex-end" : "space-between"}
        sx={{ p: list ? 0 : 2, pt: list ? 0 : 0, flexShrink: 0 }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center">
          {canRunAgents && (
            <Tooltip title="Chat with agent">
              <IconButton
                component={Link}
                to={`/agent/${agent.agent_id}/chatAgent`}
                aria-label="Chat with agent"
                size="small"
                sx={{ ...actionSx, color: copperText }}
              >
                <ChatBubbleOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="View agent details">
            <IconButton
              component={Link}
              to={`/agent/${agent.agent_id}`}
              aria-label="View agent details"
              size="small"
              sx={actionSx}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canUpdateAgents && (
            <Tooltip title="Edit agent">
              <IconButton
                component={Link}
                to="/agent/create"
                state={{ agentCard: agent }}
                aria-label="Edit agent"
                size="small"
                sx={actionSx}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canCreateAgents && (
            <Tooltip title={cloning ? "Cloning agent" : "Clone agent"}>
              <span>
                <IconButton
                  onClick={handleClone}
                  disabled={cloning}
                  aria-label="Clone agent"
                  size="small"
                  sx={actionSx}
                >
                  {cloning ? <CircularProgress size={16} /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          {canUpdateAgents && (
            <Tooltip title="Add skills">
              <IconButton
                component={Link}
                to={`/agent/${agent.agent_id}#skills`}
                aria-label="Add skills"
                size="small"
                sx={actionSx}
              >
                <AutoAwesomeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDeleteAgents && (
            <Tooltip title="Delete agent">
              <span>
                <IconButton
                  onClick={() => onDelete(agent.agent_id)}
                  disabled={deleting === agent.agent_id}
                  aria-label="Delete agent"
                  size="small"
                  sx={{
                    ...actionSx,
                    "&:hover": {
                      color: isDark ? "#FF9C91" : "#B42318",
                      borderColor: isDark ? "rgba(239,99,99,0.34)" : "rgba(180,35,24,0.35)",
                      bgcolor: isDark ? "rgba(239,99,99,0.12)" : "rgba(180,35,24,0.08)",
                    },
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Card>
  );
}

export default function NewAgentListPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilterFromUrl = normalizeStatusFilter(searchParams.get("status"));
  const collectionFromUrl = normalizeCollectionValue(searchParams.get("collection"));
  const highlightedAgentId = searchParams.get("highlightAgentId") || "";
  const [agents, setAgents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(statusFilterFromUrl);
  const [selectedCollection, setSelectedCollection] = useState(collectionFromUrl || null);
  const [activeHighlightId, setActiveHighlightId] = useState(highlightedAgentId);
  const [viewMode, setViewMode] = useState("grid");
  const [deleting, setDeleting] = useState("");
  const [error, setError] = useState("");
  const { can } = useUser();
  const canCreateAgents = Boolean(can?.(PERMISSIONS.AGENTS_CREATE));

  useEffect(() => {
    listAgents().then((data) => setAgents(data.agents || [])).catch(() => setError("Failed to load agents."));
  }, []);

  useEffect(() => {
    setSelectedStatus(statusFilterFromUrl);
  }, [statusFilterFromUrl]);

  useEffect(() => {
    if (highlightedAgentId) return;
    setSelectedCollection(collectionFromUrl || null);
  }, [collectionFromUrl, highlightedAgentId]);

  useEffect(() => {
    if (!highlightedAgentId) return;
    setActiveHighlightId(highlightedAgentId);
  }, [highlightedAgentId]);

  useEffect(() => {
    if (!highlightedAgentId || agents.length === 0) return;
    const highlightedAgent = agents.find((agent) => agent.agent_id === highlightedAgentId);
    if (!highlightedAgent) return;

    setSearchQuery("");
    setSelectedStatus("all");
    setSelectedCollection(inferCollection(highlightedAgent).id);
  }, [agents, highlightedAgentId]);

  const updateCollectionParam = (collectionId, options) => {
    const nextParams = new URLSearchParams(searchParams);
    if (collectionId) {
      nextParams.set("collection", collectionId);
    } else {
      nextParams.delete("collection");
    }
    setSearchParams(nextParams, options);
  };

  const openCollection = (collectionId) => {
    setSelectedCollection(collectionId);
    updateCollectionParam(collectionId);
  };

  const clearCollection = (options) => {
    setSelectedCollection(null);
    updateCollectionParam(null, options);
  };

  const clearCollectionForFilterChange = () => {
    if (!selectedCollection && !searchParams.has("collection")) return;
    clearCollection({ replace: true });
  };

  const handleDelete = async (agentId) => {
    if (!window.confirm(`Delete agent '${agentId}'? This cannot be undone.`)) return;
    setDeleting(agentId);
    try {
      await deleteAgent(agentId);
      setAgents((current) => current.filter((agent) => agent.agent_id !== agentId));
    } catch (e) {
      setError("Failed to delete agent.");
    }
    setDeleting("");
  };

  const filteredAgents = useMemo(() => {
    return agents.filter((agent) => {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        agent.agent_id?.toLowerCase().includes(query) ||
        agent.name?.toLowerCase().includes(query) ||
        agent.display_name?.toLowerCase().includes(query) ||
        agent.description?.toLowerCase().includes(query) ||
        agent.owner?.toLowerCase().includes(query) ||
        getAgentText(agent).includes(query);
      const matchesStatus =
        selectedStatus === "all" ||
        (selectedStatus === "setup_required" ? agentNeedsApiSetup(agent) : normalizeStatus(agent.status) === selectedStatus);
      return matchesSearch && matchesStatus;
    });
  }, [agents, searchQuery, selectedStatus]);

  const groups = useMemo(() => groupAgents(filteredAgents), [filteredAgents]);
  const activeGroup = selectedCollection ? groups.find((group) => group.id === selectedCollection) : null;
  const hasActiveFilter = Boolean(searchQuery.trim()) || selectedStatus !== "all";
  const visibleAgents = activeGroup?.agents || filteredAgents;
  const panelBg = isDark ? DARK_PANEL : "#FFFEFC";
  const panelBorder = isDark ? DARK_BORDER : BORDER_SOFT;
  const copperText = isDark ? DARK_COPPER_TEXT : COPPER_DARK;
  const mutedText = isDark ? "text.secondary" : MUTED_TEXT;

  useEffect(() => {
    if (!activeHighlightId || !visibleAgents.some((agent) => agent.agent_id === activeHighlightId)) return undefined;

    const scrollTimer = window.setTimeout(() => {
      document.getElementById(`agent-card-${activeHighlightId}`)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 80);
    const highlightTimer = window.setTimeout(() => {
      setActiveHighlightId("");
    }, 8000);

    return () => {
      window.clearTimeout(scrollTimer);
      window.clearTimeout(highlightTimer);
    };
  }, [activeHighlightId, visibleAgents]);

  return (
    <Box sx={{ px: PAGE_SHELL.paddingX, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto" }}>
      <Stack spacing={1.9} sx={{ mb: PAGE_SHELL.headerMarginBottom }}>
        <Box>
          <Typography variant="h3" component="h1" sx={PAGE_TYPOGRAPHY.heading}>Agent Catalog</Typography>
          <Typography variant="body2" sx={{ color: mutedText, mt: 0.35 }}>
            Browse built-in and custom agents by collection.
          </Typography>
        </Box>
        {canCreateAgents && (
          <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ pt: 0.2 }}>
            <Button component={Link} to="/agent/conversational" variant="contained" startIcon={<SmartToyOutlinedIcon />} sx={primaryActionButtonSx}>
              Create Agent
            </Button>
          </Stack>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={0} sx={{ p: 1.5, mb: PAGE_SHELL.sectionGap, borderRadius: UI_RADIUS.panel, border: `1px solid ${panelBorder}`, bgcolor: panelBg }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}>
          <TextField
            fullWidth
            size="small"
            sx={formControlSx}
            placeholder="Search agents, capabilities, owners, or categories..."
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value);
              clearCollectionForFilterChange();
            }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
          <FormControl size="small" sx={{ minWidth: 190, ...formControlSx }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={selectedStatus}
              label="Status"
              onChange={(event) => {
                setSelectedStatus(event.target.value);
                clearCollectionForFilterChange();
              }}
              startAdornment={<InputAdornment position="start"><FilterListIcon /></InputAdornment>}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="ready">Ready</MenuItem>
              <MenuItem value="setup_required">API Setup Needed</MenuItem>
              <MenuItem value="building">Building</MenuItem>
              <MenuItem value="created">Created</MenuItem>
            </Select>
          </FormControl>
          <ToggleButtonGroup size="small" exclusive value={viewMode} onChange={(_, value) => value && setViewMode(value)} sx={{ flexShrink: 0, "& .MuiToggleButton-root": { borderRadius: UI_RADIUS.button } }}>
            <ToggleButton value="grid" aria-label="Grid view"><ViewModuleIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="list" aria-label="List view"><ViewListIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      {!activeGroup && !hasActiveFilter ? (
        <>
          <Box sx={{ display: "grid", gridTemplateColumns: viewMode === "list" ? "1fr" : { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }, gap: viewMode === "list" ? 1.5 : 2.5 }}>
            {groups.map((group) => <CollectionCard key={group.id} group={group} viewMode={viewMode} onOpen={() => openCollection(group.id)} />)}
          </Box>
        </>
      ) : (
        <>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>{activeGroup?.label || "Matching agents"}</Typography>
              <Typography variant="body2" sx={{ color: mutedText }}>
                {activeGroup
                  ? `${activeGroup.agents.length} agents in this collection.`
                  : `${visibleAgents.length} agents match current filters.`}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              component={activeGroup ? "button" : Link}
              to={activeGroup ? undefined : "/agents"}
              onClick={() => {
                clearCollection();
                if (!activeGroup) {
                  setSelectedStatus("all");
                  setSearchQuery("");
                }
              }}
              sx={{ borderRadius: UI_RADIUS.button, borderColor: panelBorder, color: copperText, textTransform: "none", fontWeight: 850 }}
            >
              All collections
            </Button>
          </Stack>
          <Box sx={{ display: "grid", gridTemplateColumns: viewMode === "list" ? "1fr" : { xs: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" }, gap: viewMode === "list" ? 1.5 : 2.5 }}>
            {visibleAgents.map((agent) => (
              <AgentCard
                key={agent.agent_id}
                agent={agent}
                viewMode={viewMode}
                onDelete={handleDelete}
                deleting={deleting}
                highlighted={activeHighlightId === agent.agent_id}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
